import type { Registers } from '../../../../../../shared/flow-core/src/execution'
import type { FlowContext, PhaseAnswerStats } from '../../../../../../shared/player-contracts/src'

export type { FlowContext }

export const EMPTY_REGISTERS = (): Registers => ({ R1: 0, R2: 0, R3: 0 })

type DeckGroup = { id?: string; cardIds: string[] }

/** What a get-value node can read from: deck/group progress and checkbox state. */
export type GetterContext = {
  activeGroupId: string | null
  deckGroups: DeckGroup[]
  deckCardIds: string[]
  pools: Record<string, string[]>
  doneCardIds: Set<string>
  checkboxValues: Map<string, boolean[]>
  countCheckboxes: (cardId: string) => number
  /** Answers of this run (cardId → known). */
  cardAnswers: Map<string, boolean>
  /** Stored answers of the course's phases, provided by the host (course levels). */
  flowContext?: FlowContext | null
}

const countKnown = (answers: Map<string, boolean>) => [...answers.values()].filter(Boolean).length

/** Stored plus this run's answers of a phase (empty id = the level's own phase). */
function phaseStat(ctx: GetterContext, phaseId: string, field: keyof PhaseAnswerStats) {
  const current = ctx.flowContext?.currentPhaseId ?? null
  const id = phaseId || current
  if (!id) return 0
  const stored = ctx.flowContext?.phaseStats?.[id]?.[field] ?? 0
  if (id !== current) return stored
  return stored + (field === 'known' ? countKnown(ctx.cardAnswers) : ctx.cardAnswers.size)
}

const activeGroup = (ctx: GetterContext) =>
  ctx.activeGroupId ? (ctx.deckGroups.find((g) => g.id === ctx.activeGroupId) ?? null) : null

/** Cards of the active group; '__all__' means every deck card. */
const activeGroupCardIds = (ctx: GetterContext) => {
  const group = activeGroup(ctx)
  return group ? group.cardIds : ctx.activeGroupId === '__all__' ? ctx.deckCardIds : []
}

/** Value of a get-value source; unknown sources read as 0. */
export function readGetterSource(
  source: string,
  literal: unknown,
  ctx: GetterContext,
  phaseId = '',
): number {
  const groupId = ctx.activeGroupId
  switch (source) {
    case 'group-size': {
      const group = activeGroup(ctx)
      return group ? group.cardIds.length : groupId === '__all__' ? ctx.deckCardIds.length : 0
    }
    case 'deck-size':
      return ctx.deckCardIds.length
    case 'group-pool-remaining':
      return groupId ? (ctx.pools[groupId]?.length ?? 0) : 0
    case 'deck-pool-remaining':
      return ctx.deckCardIds.filter((id) => !ctx.doneCardIds.has(id)).length
    case 'group-done': {
      const groupIds = new Set(activeGroup(ctx)?.cardIds ?? [])
      return [...ctx.doneCardIds].filter((id) => groupIds.has(id)).length
    }
    case 'deck-done':
      return ctx.doneCardIds.size
    case 'all-checkboxes-done': {
      // 1 if every card in the active group has all its checkboxes ticked, else 0.
      const cardIds = activeGroupCardIds(ctx)
      const allDone =
        cardIds.length > 0 &&
        cardIds.every((id) => {
          const values = ctx.checkboxValues.get(id)
          return Array.isArray(values) && values.length > 0 && values.every(Boolean)
        })
      return allDone ? 1 : 0
    }
    case 'group-max-checkbox-ticked': {
      let max = 0
      for (const id of activeGroupCardIds(ctx)) {
        const values = ctx.checkboxValues.get(id)
        if (Array.isArray(values)) max = Math.max(max, values.filter(Boolean).length)
      }
      return max
    }
    case 'group-max-checkbox-total': {
      let max = 0
      for (const id of activeGroupCardIds(ctx)) max = Math.max(max, ctx.countCheckboxes(id))
      return max
    }
    case 'session-known':
      return countKnown(ctx.cardAnswers)
    case 'session-answered':
      return ctx.cardAnswers.size
    case 'phase-known':
      return phaseStat(ctx, phaseId, 'known')
    case 'phase-answered':
      return phaseStat(ctx, phaseId, 'answered')
    case 'literal':
      return typeof literal === 'number' ? literal : 0
    default:
      return 0
  }
}

const readRegisterOrLiteral = (
  source: string,
  registers: Registers,
  literal: unknown,
  missingLiteral: number,
  unknown: number,
) => {
  if (source === 'R1' || source === 'R2' || source === 'R3') return registers[source] ?? 0
  if (source === 'literal') return typeof literal === 'number' ? literal : missingLiteral
  return unknown
}

/** Value written by a set-value node (progress override). */
export const readSetValueInput = (input: string, registers: Registers, literal: unknown) =>
  readRegisterOrLiteral(input, registers, literal, 0, 0)

/**
 * Result of a math-op node. `+n`/`-n` always use the literal; add/sub/mul/set use the
 * operand (a register, or the literal which defaults to 1).
 */
export function applyMathOp(
  op: string,
  current: number,
  operandSource: string,
  registers: Registers,
  literal: unknown,
): number {
  const operand = readRegisterOrLiteral(operandSource, registers, literal, 1, 1)
  const lit = typeof literal === 'number' ? literal : 1
  switch (op) {
    case '+1':
      return current + 1
    case '-1':
      return current - 1
    case '+n':
      return current + lit
    case '-n':
      return current - lit
    case 'add':
      return current + operand
    case 'sub':
      return current - operand
    case 'mul':
      return current * operand
    case 'set':
      return operand
    default:
      return current
  }
}
